import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Users, Shield, Award, MapPin, Phone, Mail, CheckCircle, Trophy, Target, Heart, ChevronLeft, ChevronRight, Globe, Headphones, Eye, Crosshair, Hotel, Car, Map, CalendarCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import SEO from '../components/SEO';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { AnimatedSection, AnimatedCard, AnimatedCounter } from '../components/AnimatedComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AboutPage = () => {
  const { settings } = useSiteSettings();
  const [teamMembers, setTeamMembers] = useState([]);
  const [hoveredMember, setHoveredMember] = useState(null);
  const teamCarouselRef = useRef(null);

  const [currentValueIndex, setCurrentValueIndex] = useState(0);
  const [isValueHovered, setIsValueHovered] = useState(false);

  const contactInfo = settings?.contact_info || {};
  const aboutPage = settings?.about_page || {};

  // Default icon mapping
  const iconMap = { Target, Shield, Globe, Heart, Award, CheckCircle, Users, Headphones, Eye, Crosshair, Trophy, Clock };

  // Core Values - from settings or default
  const coreValues = (aboutPage.core_values && aboutPage.core_values.length > 0) 
    ? aboutPage.core_values.map((v, i) => ({
        title: v.title,
        description: v.description,
        icon: iconMap[v.icon] || [Target, Shield, Globe, Heart, Award, CheckCircle][i % 6]
      }))
    : [
    { 
      title: 'Built for the Future', 
      description: 'We embrace technology and innovation to make every journey smarter — from seamless bookings to real-time support. Forty years of trust, built for today\'s traveller.',
      icon: Target
    },
    { 
      title: 'Trust Through Transparency', 
      description: 'We believe in honest communication and clear expectations. No hidden costs, no surprises - just straightforward service that earns your confidence.',
      icon: Shield
    },
    { 
      title: 'Local Expertise, Global Standards', 
      description: 'Our deep roots in South India combined with international service standards ensure you get the best of both worlds - authentic experiences delivered professionally.',
      icon: Globe
    },
    { 
      title: 'People Before Profits', 
      description: 'Every decision we make prioritizes the wellbeing of our travelers, our team, and the communities we serve. Sustainable success comes from genuine care.',
      icon: Heart
    },
    { 
      title: 'Excellence in Every Detail', 
      description: 'From the first enquiry to the final farewell, we obsess over the details that transform a good trip into an unforgettable experience.',
      icon: Award
    },
    { 
      title: 'Responsibility & Accountability', 
      description: 'We own our commitments and stand behind our promises. When challenges arise, we respond with solutions, not excuses.',
      icon: CheckCircle
    }
  ];

  // Auto-rotate values carousel
  useEffect(() => {
    if (isValueHovered) return;
    const timer = setInterval(() => {
      setCurrentValueIndex((prev) => (prev + 1) % coreValues.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [isValueHovered, coreValues.length]);

  useEffect(() => {
    fetch(`${API_URL}/api/team`)
      .then(res => res.json())
      .then(data => setTeamMembers(data))
      .catch(() => {});
  }, []);

  const scrollTeamCarousel = (direction) => {
    if (teamCarouselRef.current) {
      teamCarouselRef.current.scrollBy({ left: direction === 'left' ? -300 : 300, behavior: 'smooth' });
    }
  };

  // Content from settings or defaults
  // Handle both string and object formats for our_story
  const getAboutUsContent = () => {
    const story = aboutPage.our_story;
    if (!story) {
      return `Some brands are built from scratch. Sentora Holidays was built on forty years of earned trust.`;
    }
    if (typeof story === 'string') {
      return story;
    }
    // If it's an object with p1, p2, p3 keys, combine them
    if (typeof story === 'object') {
      const parts = [];
      if (story.p1) parts.push(story.p1);
      if (story.p2) parts.push(story.p2);
      if (story.p3) parts.push(story.p3);
      return parts.join('\n\n');
    }
    return `Some brands are built from scratch. Sentora Holidays was built on forty years of earned trust.`;
  };
  
  const aboutUsContent = getAboutUsContent() || `Some brands are built from scratch. Sentora Holidays was built on forty years of earned trust.

We are the travel and destination management arm of SAM Tours & Travels - one of South India's most established names in ground transportation and travel services. Since 1985, SAM Tours has moved millions of travellers across the region with a fleet of 150+ vehicles and a reputation built on reliability, discretion, and local expertise. That foundation is what Sentora Holidays stands on.

As the official travel desk partner for Taj Hotels across Tamil Nadu and Karnataka, we have operated at the highest standard of hospitality service for years - managing the movement and experiences of guests who expect nothing less than seamless. That relationship is not a credential we display. It is a discipline we carry into every programme we design.

Sentora Holidays was created to bring that same depth and standard to the wider world of destination management. We design and operate complete ground programmes across India - from the backwaters of Kerala and the temple towns of Tamil Nadu to the palaces of Rajasthan, the valleys of Himachal, and the living heritage of the Northeast. Every region we cover, we cover with care.

But South India is where we are rooted. It is the geography we know most intimately - its roads, its rhythms, its hidden stays, and its finest local voices. Our knowledge of South India is not researched. It is lived.`;

  const coreStrengths = [
    { 
      icon: Users, 
      title: 'Experienced Team', 
      description: 'Decades of experience in travel planning, ground operations, and hospitality.' 
    },
    { 
      icon: Globe, 
      title: 'Pan-India Network', 
      description: 'Established partnerships and on-ground presence across all 28 states and 8 union territories.' 
    },
    { 
      icon: Shield, 
      title: 'Trusted & Reliable', 
      description: 'Ministry of Tourism recognised, IATO accredited, and ISO certified with a 40-year track record to match.' 
    },
    { 
      icon: Headphones, 
      title: '24/7 Support', 
      description: 'A dedicated team available round the clock for every programme we handle, from departure to return.' 
    }
  ];

  const achievements = [
    'Ministry of Tourism Recognized',
    'IATO Accredited Member',
    'ISO 9001:2015 Certified',
    'Official Travel Partner - Taj Hotels (Tamil Nadu & Karnataka)',
    '40+ Years of Excellence',
    '150+ Vehicle Fleet',
    '5 Lakh+ Happy Travelers'
  ];

  const vision = aboutPage.vision || `To be India's most trusted destination management company — building travel experiences that are seamless, responsible, and built for the future. We are committed to growing alongside the communities and destinations we serve, ensuring that every journey we design leaves a positive impact on the places and people that make it possible.`;

  const mission = aboutPage.mission || `To make travel in India effortless, meaningful, and done right. We handle every detail on the ground — transport, stays, guides, logistics — so our partners and travellers can focus on the experience itself. Rooted in four decades of trust, we are building something that works for today's traveller and tomorrow's world.`;

  const directorMotto = aboutPage.director_motto || `Great travel is not just about the destination. It is about every detail handled right, every commitment honoured, and every person treated with respect. Do it with honesty. Do it with heart. Do it right.`;
  
  const directorName = aboutPage.director_name || '';
  const directorPhoto = aboutPage.director_photo || '';

  return (
    <div className="min-h-screen bg-s-bg">
      <SEO 
        title="About Sentora Holidays - 40 Years of Trusted Travel Excellence" 
        description="Learn about Sentora Holidays' 40-year journey as India's trusted destination management company. Official partner of Taj Hotels, IATO accredited, ISO certified." 
      />

      {/* Hero Section */}
      <section className="relative min-h-[35vh] md:min-h-[40vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=1920&q=80" 
            alt="Travel journey" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-s-bg/90 via-s-bg/80 to-s-bg" />
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-6 py-12 md:py-16 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-s-text font-heritage italic mb-4 hero-text-reveal">
            About Us
          </h1>
          <p className="text-s-text2 text-lg md:text-xl max-w-2xl mx-auto hero-text-reveal-delay">
            Four decades of trust. One commitment to excellence.
          </p>
        </div>
      </section>

      {/* Services Highlight - What We Offer */}
      <section className="py-8 md:py-10 px-4 bg-s-bg2 border-y border-s-border">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { icon: Hotel, title: 'Hotels', desc: 'Curated Stays' },
              { icon: Users, title: 'Expert Guides', desc: 'Local Knowledge' },
              { icon: Car, title: 'Transport', desc: 'Reliable Fleet' },
              { icon: CalendarCheck, title: 'Complete Itinerary', desc: 'End-to-End Planning' }
            ].map((service, idx) => (
              <div key={idx} className="flex flex-col items-center text-center p-4 rounded-xl hover:bg-s-bg transition-colors">
                <div className="w-12 h-12 rounded-full bg-s-primary/10 flex items-center justify-center mb-3">
                  <service.icon size={22} className="text-s-primary" />
                </div>
                <h3 className="text-s-text font-semibold text-sm md:text-base">{service.title}</h3>
                <p className="text-s-text2 text-xs mt-1">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 1. About Us - Our Story */}
      <section className="py-12 md:py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <AnimatedSection className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-s-text font-heritage italic mb-2">Our Story</h2>
            <div className="w-16 h-1 bg-gradient-to-r from-s-primary to-s-primary mx-auto rounded-full"></div>
          </AnimatedSection>
          
          <AnimatedSection delay={200} className="relative">
            {/* Decorative quote mark */}
            <div className="absolute -top-4 -left-4 text-6xl md:text-8xl text-s-primary/10 font-serif leading-none select-none">"</div>
            
            <div className="bg-s-bg2/50 backdrop-blur-sm border border-s-border rounded-2xl p-6 md:p-10 lg:p-12">
              <div className="space-y-6 md:space-y-8">
                {aboutUsContent.split('\n\n').map((paragraph, idx) => (
                  <p 
                    key={idx} 
                    className={`text-s-text2 leading-[1.9] md:leading-[2] ${
                      idx === 0 
                        ? 'text-lg md:text-xl font-medium text-s-text' 
                        : 'text-base md:text-lg'
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
              
              {/* Bottom accent line */}
              <div className="mt-8 pt-6 border-t border-s-border/50 flex items-center justify-center gap-4">
                <div className="h-px w-12 bg-gradient-to-r from-transparent to-s-primary/50"></div>
                <span className="text-s-primary text-sm font-medium tracking-wider uppercase">40 Years of Trust</span>
                <div className="h-px w-12 bg-gradient-to-l from-transparent to-s-primary/50"></div>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 2. Core Strengths */}
      <section className="py-16 md:py-20 px-4 bg-s-bg2">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-s-text font-heritage italic">Core Strengths</h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreStrengths.map((strength, idx) => (
              <AnimatedCard key={idx} delay={idx * 100} hoverEffect="lift-subtle" className="bg-s-card border-s-border rounded-xl">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-s-primary/10 flex items-center justify-center">
                    <strength.icon size={24} className="text-s-primary" />
                  </div>
                  <h3 className="text-s-text font-bold text-lg mb-2">{strength.title}</h3>
                  <p className="text-s-text2 text-sm leading-relaxed">{strength.description}</p>
                </CardContent>
              </AnimatedCard>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Awards and Achievements */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-s-text font-heritage italic">Awards & Achievements</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {achievements.map((achievement, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-s-card p-5 rounded-xl border border-s-border hover:border-s-primary/30 transition-all">
                <div className="w-12 h-12 bg-gradient-to-br from-s-primary to-s-primary/70 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <p className="text-s-text font-medium">{achievement}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Our Values - Carousel */}
      <section className="py-16 md:py-20 px-4 bg-s-bg2 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-s-text font-heritage italic">Our Values</h2>
            <p className="text-s-text2 mt-2 max-w-xl mx-auto">The principles that guide every journey we craft.</p>
          </AnimatedSection>

          {/* Values Carousel */}
          <AnimatedSection delay={200}>
            <div 
              className="relative"
              onMouseEnter={() => setIsValueHovered(true)}
              onMouseLeave={() => setIsValueHovered(false)}
            >
              <div className="flex items-center justify-center min-h-[300px]">
                {coreValues.map((value, idx) => {
                  const isActive = idx === currentValueIndex;
                  const isPrev = idx === (currentValueIndex - 1 + coreValues.length) % coreValues.length;
                  const isNext = idx === (currentValueIndex + 1) % coreValues.length;
                  
                  let transform = 'scale(0.6) translateX(0)';
                  let zIndex = 0;
                  let opacity = 0;
                  
                  if (isActive) {
                    transform = 'scale(1) translateX(0)';
                    zIndex = 30;
                    opacity = 1;
                  } else if (isPrev) {
                    transform = 'scale(0.75) translateX(-120%)';
                    zIndex = 20;
                    opacity = 0.5;
                  } else if (isNext) {
                    transform = 'scale(0.75) translateX(120%)';
                    zIndex = 20;
                    opacity = 0.5;
                  }

                const IconComponent = value.icon;

                return (
                  <div
                    key={idx}
                    className="absolute transition-all duration-500 ease-out cursor-pointer"
                    style={{ transform, zIndex, opacity }}
                    onClick={() => setCurrentValueIndex(idx)}
                  >
                    <Card className={`w-[320px] md:w-[400px] ${isActive ? 'shadow-xl border-s-primary/30' : 'border-s-border'}`}>
                      <CardContent className="p-6 md:p-8 text-center">
                        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-s-primary/10 flex items-center justify-center">
                          <IconComponent size={28} className="text-s-primary" />
                        </div>
                        <h3 className="text-s-text font-bold text-lg md:text-xl mb-3 font-heritage">{value.title}</h3>
                        <p className="text-s-text2 text-sm md:text-base leading-relaxed">{value.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                );
              })}
            </div>

            {/* Navigation */}
            <button
              onClick={() => setCurrentValueIndex((prev) => (prev === 0 ? coreValues.length - 1 : prev - 1))}
              className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 bg-s-bg border border-s-border hover:border-s-primary/50 rounded-full flex items-center justify-center transition-all"
            >
              <ChevronLeft size={20} className="text-s-text" />
            </button>
            <button
              onClick={() => setCurrentValueIndex((prev) => (prev === coreValues.length - 1 ? 0 : prev + 1))}
              className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 bg-s-bg border border-s-border hover:border-s-primary/50 rounded-full flex items-center justify-center transition-all"
            >
              <ChevronRight size={20} className="text-s-text" />
            </button>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {coreValues.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentValueIndex(idx)}
                className={`h-2 rounded-full transition-all ${idx === currentValueIndex ? 'bg-s-primary w-6' : 'bg-s-muted w-2'}`}
              />
            ))}
          </div>
          </AnimatedSection>
        </div>
      </section>

      {/* 5. Vision & Mission */}
      <section className="py-16 md:py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <AnimatedCard delay={0} hoverEffect="lift-subtle" className="bg-gradient-to-br from-s-primary/5 to-s-primary/10 border-s-primary/20 rounded-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-s-primary/20 flex items-center justify-center">
                    <Eye size={24} className="text-s-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-s-text font-heritage italic">Vision</h3>
                </div>
                <p className="text-s-text2 text-base leading-relaxed">
                  {vision}
                </p>
              </CardContent>
            </AnimatedCard>

            {/* Mission */}
            <AnimatedCard delay={150} hoverEffect="lift-subtle" className="bg-gradient-to-br from-s-primary/5 to-s-primary/10 border-s-primary/20 rounded-xl">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-full bg-s-primary/20 flex items-center justify-center">
                    <Crosshair size={24} className="text-s-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-s-text font-heritage italic">Mission</h3>
                </div>
                <p className="text-s-text2 text-base leading-relaxed">
                  {mission}
                </p>
              </CardContent>
            </AnimatedCard>
          </div>
        </div>
      </section>

      {/* 6. Director's Motto */}
      <section className="py-16 md:py-20 px-4 bg-s-bg2">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-s-text font-heritage italic mb-12 text-center">Director's Motto</h2>
          
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Quote on Left */}
            <div className="relative bg-s-card p-8 md:p-10 rounded-2xl border border-s-border">
              <span className="absolute top-4 left-4 text-6xl md:text-7xl text-s-primary/20 font-serif leading-none">"</span>
              <blockquote className="text-s-text text-lg md:text-xl leading-relaxed font-medium pt-8 px-4">
                {directorMotto}
              </blockquote>
              <span className="absolute bottom-4 right-4 text-6xl md:text-7xl text-s-primary/20 font-serif leading-none">"</span>
            </div>

            {/* Director's Photo on Right */}
            <div className="flex flex-col items-center">
              <div className="w-64 h-80 md:w-72 md:h-96 rounded-2xl overflow-hidden border-4 border-s-primary/20 shadow-xl">
                <img 
                  src={directorPhoto || "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=600&q=80"} 
                  alt={directorName || "Director - Sentora Holidays"} 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="mt-4 text-center">
                <p className="text-s-text font-bold text-xl">{directorName || "Managing Director"}</p>
                <p className="text-s-text2 text-sm">SAM Tours & Travels / Sentora Holidays</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. Our Team */}
      {teamMembers.length > 0 && (
        <section className="py-16 md:py-20 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-s-text font-heritage italic">Our Team</h2>
              <p className="text-s-text2 mt-2 max-w-2xl mx-auto">
                The experienced professionals behind every seamless journey.
              </p>
            </div>

            <div className="relative group">
              <button 
                onClick={() => scrollTeamCarousel('left')} 
                className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-s-bg/90 hover:bg-s-bg border border-s-border p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2"
              >
                <ChevronLeft className="text-s-text" size={20} />
              </button>
              <button 
                onClick={() => scrollTeamCarousel('right')} 
                className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-s-bg/90 hover:bg-s-bg border border-s-border p-3 rounded-full opacity-0 group-hover:opacity-100 transition-opacity translate-x-2"
              >
                <ChevronRight className="text-s-text" size={20} />
              </button>
              
              <div 
                ref={teamCarouselRef} 
                className="flex gap-6 overflow-x-auto pb-4 scroll-smooth snap-x snap-mandatory" 
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {teamMembers.map((member) => (
                  <div
                    key={member.id}
                    className="flex-shrink-0 w-[220px] snap-start"
                    onMouseEnter={() => setHoveredMember(member.id)}
                    onMouseLeave={() => setHoveredMember(null)}
                  >
                    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden bg-s-card border border-s-border hover:border-s-primary/50 transition-all">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 transition-all ${hoveredMember === member.id ? 'pb-6' : ''}`}>
                        <p className="text-white font-bold text-base">{member.name}</p>
                        <p className="text-white/80 text-sm">{member.role}</p>
                        {member.years_of_experience > 0 && (
                          <p className="text-s-primary text-xs mt-1">{member.years_of_experience}+ Years Experience</p>
                        )}
                        {hoveredMember === member.id && member.specialization && (
                          <p className="text-white/60 text-xs mt-2">Specializes in: {member.specialization}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact CTA */}
      <section className="py-16 md:py-20 px-4 bg-gradient-to-r from-s-primary to-s-primary">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 font-heritage italic">
            Ready to Experience the Sentora Holidays Difference?
          </h2>
          <p className="text-lg md:text-xl text-white/80 mb-8">
            Let us handle every detail of your next journey.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="w-full sm:w-auto bg-white text-s-primary hover:bg-gray-100 text-lg px-8 h-14 font-bold rounded-full">
                <Mail className="mr-2" size={20} /> Contact Us
              </Button>
            </Link>
            <a href={`tel:${contactInfo.phone}`}>
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10 text-lg px-8 h-14 rounded-full">
                <Phone className="mr-2" size={20} /> {contactInfo.phone || 'Call Us'}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
